/** Insumos de la bodega central: servilletas, bombillas, vasos de
 *  helado, bolsas. Cosas que salen de la misma bodega que la fruta pero
 *  que no se le venden a nadie.
 *
 *  Viven en su propia tabla —no como productos con un rubro distinto—
 *  porque no tienen precio de venta, ni lote, ni vencimiento, ni lista
 *  mayorista, y porque no deben aparecer jamás en la pantalla de vender
 *  ni en el catálogo del local. Estando separados, eso es imposible por
 *  construcción en vez de depender de un filtro que alguien olvide.
 *
 *  Igual que los datos de POS, esto NO va en el `Snapshot` del store:
 *  solo los mira el administrador, y hacer que cada vendedor los
 *  descargue al entrar solo alargaría un arranque que ya es lento.
 *
 *  El esquema está en `supabase/insumos.sql`.
 */

import { supabase } from './supabaseAdapter'
import { traducirError } from './pos'

/* --------------------------- Tipos --------------------------- */

export interface Insumo {
  id: string
  codigo: string
  nombre: string
  /** Texto libre: "unidad", "caja", "paquete", "rollo". Los insumos no
   *  comparten las unidades de la fruta. */
  unidad: string
  stock: number
  stockMinimo: number
  costoUnitario: number
  proveedor: string | null
  notas: string | null
  /** URL de datos (base64), igual que la foto de un producto —no hay
   *  bucket de Supabase Storage, se guarda directo en la columna. */
  foto?: string | null
  activo: boolean
  creado: string
  actualizado: string
}

export interface MovimientoInsumo {
  id: string
  insumoId: string
  tipo: 'entrada' | 'salida' | 'ajuste' | 'traspaso'
  cantidad: number
  stockResultante: number
  referenciaId: string | null
  usuarioId: string | null
  usuarioNombre: string | null
  observaciones: string | null
  fecha: string
}

/** Una línea de insumo dentro de un envío a sucursal. */
export interface InsumoDeTraspaso {
  id: string
  traspasoId: string
  insumoId: string
  nombre: string
  cantidad: number
  costoUnitario: number
}

export const UNIDADES_INSUMO = ['unidad', 'caja', 'paquete', 'rollo', 'bolsa', 'kilo', 'litro']

/* --------------------------- Utilidades --------------------------- */

function sb() {
  if (!supabase) throw new Error('Supabase no está configurado.')
  return supabase
}

/** Traduce los errores de "todavía no corriste la migración".
 *
 *  Son dos códigos distintos y hay que mirar los dos: Postgres devuelve
 *  `42P01` cuando la tabla no existe, pero PostgREST corta antes y
 *  responde `PGRST205` —"no está en el schema cache"— en inglés y con
 *  el nombre técnico de la tabla. Ese mensaje no le sirve a nadie. */
function traducir(e: { code?: string; message: string }): string {
  const faltaLaTabla =
    e.code === '42P01' || e.code === 'PGRST205' || /schema cache/i.test(e.message)
  if (faltaLaTabla) {
    return 'Los insumos todavía no están instalados en esta base de datos. Hay que correr supabase/insumos.sql.'
  }
  if (e.code === 'PGRST202') {
    return 'Falta actualizar la base de datos: no existe la función de insumos. Hay que correr supabase/insumos.sql.'
  }
  if (e.code === '23505') {
    return 'Ya existe un insumo con ese código.'
  }
  return traducirError(e)
}

async function leer<T>(
  consulta: PromiseLike<{ data: unknown; error: { code?: string; message: string } | null }>,
): Promise<T[]> {
  const { data, error } = await consulta
  if (error) throw new Error(traducir(error))
  return (data as T[] | null) ?? []
}

/* --------------------------- Lecturas --------------------------- */

export async function traerInsumos(): Promise<Insumo[]> {
  return leer<Insumo>(sb().from('insumos').select('*').order('nombre'))
}

export async function traerMovimientos(insumoId: string, limite = 50): Promise<MovimientoInsumo[]> {
  return leer<MovimientoInsumo>(
    sb()
      .from('insumo_movimientos')
      .select('*')
      .eq('insumoId', insumoId)
      .order('fecha', { ascending: false })
      .limit(limite),
  )
}

/** Los insumos que llevaba cada envío, para poder mostrarlos junto a la
 *  fruta en la ficha de la sucursal. */
export async function traerInsumosDeTraspasos(traspasoIds: string[]): Promise<InsumoDeTraspaso[]> {
  if (!traspasoIds.length) return []
  return leer<InsumoDeTraspaso>(
    sb().from('pos_traspaso_insumos').select('*').in('traspasoId', traspasoIds),
  )
}

/* --------------------------- Escrituras --------------------------- */

/** Crea o edita la ficha de un insumo.
 *
 *  Nunca manda `stock`: esa columna la mueve solo `moverStock`, porque
 *  entre que la pantalla cargó y el administrador apretó guardar puede
 *  haber salido un traspaso. Guardar la fila entera con la copia vieja
 *  devolvería el stock hacia atrás sin ningún error visible —es
 *  exactamente el problema que ya se sufrió con `productos`. */
export async function guardarInsumo(
  insumo: Omit<Insumo, 'stock' | 'creado' | 'actualizado'> & { creado?: string },
): Promise<void> {
  const { error } = await sb()
    .from('insumos')
    .upsert({
      id: insumo.id,
      codigo: insumo.codigo,
      nombre: insumo.nombre,
      unidad: insumo.unidad,
      stockMinimo: insumo.stockMinimo,
      costoUnitario: insumo.costoUnitario,
      proveedor: insumo.proveedor || null,
      notas: insumo.notas || null,
      foto: insumo.foto || null,
      activo: insumo.activo,
      actualizado: new Date().toISOString(),
    })
  if (error) throw new Error(traducir(error))
}

export interface CambioInsumo {
  id: string
  insumoId: string
  tipo: MovimientoInsumo['tipo']
  /** Cuánto se mueve: positivo suma, negativo resta. */
  cantidad?: number
  /** Conteo físico: el stock queda exactamente en este número y el
   *  delta lo calcula la base contra lo que hay guardado. */
  stockFinal?: number
  referenciaId?: string
  usuarioId?: string
  usuarioNombre?: string
  observaciones?: string
}

/** Mueve el stock de uno o varios insumos y deja el movimiento anotado,
 *  todo en la misma transacción. Devuelve cómo quedó el stock de cada
 *  uno para que la pantalla pise su copia con lo que dice la base. */
export async function moverStock(cambios: CambioInsumo[]): Promise<Record<string, number>> {
  const { data, error } = await sb().rpc('nb_mover_insumo', { p_cambios: cambios })
  if (error) throw new Error(traducir(error))
  const resultado = (data ?? {}) as { stock?: Record<string, number> }
  return resultado.stock ?? {}
}

export async function eliminarInsumo(id: string): Promise<void> {
  const { error } = await sb().from('insumos').delete().eq('id', id)
  if (error) throw new Error(traducir(error))
}
